import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import * as Parse from 'parse';
import * as Sentry from '@sentry/react';

import {AppThunk} from 'state';
import {fetchBranches, updateBranchFilter} from 'slices/dashboardFilters';

type Profile = {
  id: string;
  name: string | null;
  country: string | null;
  currency: string | null;
  currencySymbol: string | null;
  available: boolean;
  coverPhoto: string | null;
  menu:
    | {
        id: string;
        name: string;
      }[]
    | null;
  reviews: string[] | null;
  password: string | null;
  timeZone: string;
  serviceFees: number;
  isHotel: boolean;
  menuPassword: string | null;
  dailyWorkingHours: any;
};

interface State {
  profiles: Profile[];
  error: string | null;
  fetchLoading: boolean;
  updateLoading: boolean;
  successNotification: boolean;
}

const initialState: State = {
  profiles: [],
  error: null,
  fetchLoading: false,
  updateLoading: false,
  successNotification: false,
};

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    fetchProfileStart(state) {
      state.fetchLoading = true;
      state.error = null;
    },
    fetchProfileSuccess(state, action: PayloadAction<Profile[]>) {
      state.profiles = action.payload;
      state.fetchLoading = false;
    },
    fetchProfileFailed(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.fetchLoading = false;
    },
    reset(state) {
      state.profiles = [];
      state.updateLoading = false;
      state.error = null;
      state.fetchLoading = false;
      state.successNotification = false;
    },
  },
});

export const {
  fetchProfileStart,
  fetchProfileSuccess,
  fetchProfileFailed,
  reset,
} = profileSlice.actions;

export default profileSlice.reducer;

/**
 * Thunks
 */

export const fetchProfile =
  (restaurantIds?: string[]): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchProfileStart());

      let restaurants: Parse.Object<Parse.Attributes>[] = [];

      // If restaurantIds are provided, fetch those restaurants directly
      if (restaurantIds && restaurantIds.length > 0) {
        const restaurantQuery = new Parse.Query('Restaurant');
        restaurantQuery.containedIn('objectId', restaurantIds);
        restaurants = await restaurantQuery.find();
      } else {
        // Otherwise, check if the user has a restaurant or entity
        const currentUser = Parse.User.current()!;
        let userRestaurant = await currentUser.get('restaurant');
        if (userRestaurant) {
          restaurants = [await userRestaurant.fetch()];
          // Only update branchFilter if it's not already set (don't override user selection)
          const currentState = getState();
          if (
            !currentState.dashboardFilters.branchFilter ||
            currentState.dashboardFilters.branchFilter.length === 0
          ) {
            dispatch(updateBranchFilter({branchFilter: [restaurants[0].id]}));
          }

        } else {
          const entity = await currentUser.get('entity');
          if (entity) {
            const entityDetails = await entity.fetch();
            const branches = entityDetails.get('branches');

            if (branches && branches.length > 0) {
              dispatch(fetchBranches());
              // Retrieve restaurant objectIds from all branches
              const branchIds = branches
                .map((branch: any) => branch.id)
                .filter(Boolean);
              if (branchIds.length > 0) {
                const restaurantQuery = new Parse.Query('Restaurant');
                restaurantQuery.containedIn('objectId', branchIds);
                restaurants = await restaurantQuery.find();

                // Only update branch filter if it's not already set (don't override user selection)
                const currentState = getState();
                if (
                  !currentState.dashboardFilters.branchFilter ||
                  currentState.dashboardFilters.branchFilter.length === 0
                ) {
                  if (branchIds[0]) {
                    dispatch(updateBranchFilter({branchFilter: [branchIds[0]]}));
                  }
                }
              }
            }
          }
        }
      }

      if (restaurants.length > 0) {
        const profiles = restaurants.map((restaurant) => {
          const {
            id,
            attributes: {
              name,
              country,
              currency,
              currencySymbol,
              available,
              coverPhoto,
              menu,
              reviewsPoints,
              password,
              menuPassword,
              timeZone,
              dailyWorkingHours,
            },
          } = restaurant;

          return {
            id,
            name,
            country,
            currency,
            currencySymbol,
            available,
            coverPhoto: coverPhoto && coverPhoto._url,
            menu: menu.map((item: any) => ({id: item.id, name: item.name})),
            reviews: reviewsPoints,
            password,
            timeZone: timeZone || 'Europe/Paris',
            serviceFees: restaurant.get('serviceFees') || 0,
            isHotel: restaurant.get('isHotel') || false,
            menuPassword,
            dailyWorkingHours,
          };
        });

        dispatch(fetchProfileSuccess(profiles));
      } else {
        dispatch(fetchProfileFailed('No restaurants or branches found'));
      }
    } catch (error) {
      dispatch(fetchProfileFailed(error.toString()));
      Sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('fetchProfile error', error);
    }
  };
